// apps/roleforge-app/src/components/invitations/StakeholderInviteForm.tsx
// Plan 3b-ii Phase 8.5: Form to invite a stakeholder to complete an async interview.

import { useState, type FormEvent } from 'react';
import { createInvitation, type InvitationRow } from '../../lib/api-client';
import { parseCapError, type CapError } from '../../lib/cap-error';
import { CapExceededModal } from '../cap/CapExceededModal';
import { useAuth } from '../../lib/auth';

interface Props {
  auditId: string;
  /**
   * Issue #1304: `respondUrl` is the one-time invite link. The server stores
   * only `sha256(token)`, so this is the only moment the URL exists outside the
   * recipient's inbox — hand it up so the page can offer "copy link" for this
   * invitation and no other.
   */
  onCreated: (invitation: InvitationRow, respondUrl?: string) => void;
}

export function StakeholderInviteForm({ auditId, onCreated }: Props) {
  const { jwt } = useAuth();
  const [email, setEmail] = useState('');
  const [roleHint, setRoleHint] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [capError, setCapError] = useState<CapError | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!jwt || !email.trim() || submitting) return;

    setSubmitting(true);
    setError(null);

    try {
      const result = await createInvitation(jwt, auditId, {
        email: email.trim(),
        role_hint: roleHint.trim() || undefined,
      });
      setEmail('');
      setRoleHint('');
      onCreated(result.invitation, result.respond_url);
    } catch (err: unknown) {
      const cap = parseCapError(err);
      if (cap) {
        setCapError(cap);
      } else {
        const message =
          err instanceof Error ? err.message : 'Failed to send invitation';
        setError(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <label htmlFor="invite-email" className="text-sm font-medium text-ink">
            Email address
          </label>
          <input
            id="invite-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="stakeholder@company.com"
            className="rounded-md border border-line bg-surface px-4 py-3 text-sm text-ink placeholder:text-graphite outline-none transition focus:border-coral focus:ring-2 focus:ring-coral/35"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="invite-role-hint" className="text-sm font-medium text-ink">
            Role <span className="text-graphite font-normal">(optional)</span>
          </label>
          <input
            id="invite-role-hint"
            type="text"
            value={roleHint}
            onChange={(e) => setRoleHint(e.target.value)}
            placeholder="e.g. Operations Manager"
            className="rounded-md border border-line bg-surface px-4 py-3 text-sm text-ink placeholder:text-graphite outline-none transition focus:border-coral focus:ring-2 focus:ring-coral/35"
          />
        </div>
        {error && (
          <p role="alert" className="rounded-md border border-ember/40 bg-ember/10 px-3 py-2 text-sm text-ember">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={submitting || !email.trim()}
          className="inline-flex items-center justify-center rounded-md bg-coral px-4 py-3 text-sm font-semibold text-white transition hover:bg-coral/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitting ? 'Sending...' : 'Send invitation'}
        </button>
      </form>

      {capError && (
        <CapExceededModal error={capError} onClose={() => setCapError(null)} />
      )}
    </>
  );
}
